
import { Stack } from 'ui/stack';

import IconButton from 'components/partials/button/icon-button';
import Select from 'components/partials/select/select';

import Icons from 'assets/icons';

import type {
  PopulationCriteriaBlock as CriteriaBlock,
  PopulationCriteriaBlockErrors,
  SelectOption,
} from '../hooks/use-populations-draft';

export interface PopulationCriteriaBlockProps {
  block: CriteriaBlock;
  errors?: PopulationCriteriaBlockErrors;
  dimensionOptions: SelectOption[];
  valueOptions: SelectOption[];
  onDimensionChange: (blockId: string, dimension: string | null) => void;
  onValuesChange: (blockId: string, values: string[]) => void;
  onRemove: (blockId: string) => void;
}

const PopulationCriteriaBlock = ({
  block,
  errors,
  dimensionOptions,
  valueOptions,
  onDimensionChange,
  onValuesChange,
  onRemove,
}: PopulationCriteriaBlockProps) => {
  const { blockId, dimension, values } = block;
  const headingId = `${blockId}-heading`;
  const selectedDimensionOption = dimensionOptions.find(
    (opt) => opt.value === dimension
  );
  const selectedValueOptions = valueOptions.filter((opt) =>
    values.includes(opt.value)
  );
  const valuesLabel = dimension ? `Value(s) for ${dimension}` : 'Value(s)';

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-3 rounded-lg border border-navy-100 p-4"
      data-testid={blockId}
    >
      <Stack
        direction="row"
        sx={{
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h3 id={headingId} className="m-0 text-md font-semibold">
          Criteria
        </h3>
        <IconButton
          icon={<Icons.Delete />}
          variant="reserve-text"
          size="sm"
          aria-label="Remove criteria"
          onClick={() => onRemove(blockId)}
          className="absolute -right-1.5"
          data-testid={`${blockId}-remove`}
        />
      </Stack>

      <Select<SelectOption>
        label="Dimension"
        labelInTop
        required
        options={dimensionOptions}
        placeholder=" "
        value={selectedDimensionOption ?? undefined}
        onChange={(option) => {
          if (option && !Array.isArray(option)) {
            onDimensionChange(blockId, option.value);
          }
        }}
        fieldError={
          errors?.dimension ? { message: 'Select a dimension' } : undefined
        }
        customClass="mt-0"
        extraBasicStyles={{ minHeight: 40, padding: 0 }}
        data-testid={`${blockId}-dimension`}
      />

      <Select<SelectOption>
        label={valuesLabel}
        labelInTop
        required
        options={valueOptions}
        isMulti
        isClearable
        disabled={!dimension}
        placeholder=" "
        value={selectedValueOptions}
        onChange={(options) => {
          onValuesChange(
            blockId,
            Array.isArray(options) ? options.map((opt) => opt.value) : []
          );
        }}
        fieldError={
          errors?.values
            ? { message: `Select at least one value for ${dimension}` }
            : undefined
        }
        customClass="mt-0"
        extraBasicStyles={{ minHeight: 40, padding: 0 }}
        data-testid={`${blockId}-values`}
      />
    </section>
  );
};

export default PopulationCriteriaBlock;
